import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import api from '../services/api';

import { IOption } from '../components/SelectSync';
import { useToast } from './toast';
import getResponseErrors from '../utils/getResponseErrors';

export interface IFormaPagamento {
  BanCodigo: number;
  BanNome: string;
  BanSaldo?: number;
  BanTipo?: boolean;
  BanAtivo?: boolean;
}

interface IOptionsFormasPagamento {
  optionFormaPagamento: IOption[];
}
interface IFormasPagamento {
  formasPagamento: IFormaPagamento[];
}

interface FormaPagamentoContextData {
  loading: boolean;
  optionsFormasPagamento: IOptionsFormasPagamento;
  optionsFormasPagamentoTipo1: IOptionsFormasPagamento;
  formasPagamento: IFormasPagamento;
  buscaFormaPagamento(): Promise<void>;
  limparFormaPagamento(): void;
}

const FormaPagamentoContext = createContext<FormaPagamentoContextData>(
  {} as FormaPagamentoContextData,
);

const FormaPagamentoProvider: React.FC = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();
  const [formasPagamento, setFormasPagamento] = useState<IFormasPagamento>(
    () => {
      const formaPags = localStorage.getItem('@AgendaFacil:formasPagamento');
      if (formaPags) return JSON.parse(formaPags);

      return {} as IFormasPagamento;
    },
  );

  const [
    optionsFormasPagamento,
    setOptionsFormasPagamento,
  ] = useState<IOptionsFormasPagamento>(() => {
    const optionFormaPags = localStorage.getItem(
      '@AgendaFacil:optionsFormasPagamento',
    );
    if (optionFormaPags) return JSON.parse(optionFormaPags);

    return {} as IOptionsFormasPagamento;
  });

  const [
    optionsFormasPagamentoTipo1,
    setOptionsFormasPagamentoTipo1,
  ] = useState<IOptionsFormasPagamento>(() => {
    const optionFormaPags = localStorage.getItem(
      '@AgendaFacil:optionsFormasPagamentoTipo1',
    );
    if (optionFormaPags) return JSON.parse(optionFormaPags);

    return {} as IOptionsFormasPagamento;
  });

  const buscaFormaPagamento = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<IFormaPagamento[]>(
        `/formaPagamento?BanAtivo=true`,
      );

      const options = response.data.map((op: IFormaPagamento) => ({
        value: op.BanCodigo.toString(),
        label: op.BanNome,
      }));

      const optionsTipo1 = response.data
        .filter(op => op.BanTipo)
        .map((op: IFormaPagamento) => ({
          value: op.BanCodigo.toString(),
          label: op.BanNome,
        }));

      const localformasPagamento: IFormasPagamento = {
        formasPagamento: response.data,
      };

      const localOptions: IOptionsFormasPagamento = {
        optionFormaPagamento: options,
      };

      const localOptionsTipo1: IOptionsFormasPagamento = {
        optionFormaPagamento: optionsTipo1,
      };

      localStorage.setItem(
        '@AgendaFacil:formasPagamento',
        JSON.stringify(localformasPagamento),
      );
      localStorage.setItem(
        '@AgendaFacil:optionsFormasPagamento',
        JSON.stringify(localOptions),
      );
      localStorage.setItem(
        '@AgendaFacil:optionsFormasPagamentoTipo1',
        JSON.stringify(localOptionsTipo1),
      );

      setFormasPagamento(localformasPagamento);

      setOptionsFormasPagamento(localOptions);
      setOptionsFormasPagamentoTipo1(localOptionsTipo1);
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível carregar formas de pagamento',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    async function load(): Promise<void> {
      const tokenLocal = localStorage.getItem('@AgendaFacil:token');
      if (tokenLocal) {
        buscaFormaPagamento();
      }
    }
    load();
  }, [buscaFormaPagamento]);

  const limparFormaPagamento = useCallback(() => {
    localStorage.removeItem('@AgendaFacil:formasPagamento');
    localStorage.removeItem('@AgendaFacil:optionsFormasPagamento');
    localStorage.removeItem('@AgendaFacil:optionsFormasPagamentoTipo1');
    setOptionsFormasPagamento({} as IOptionsFormasPagamento);
    setOptionsFormasPagamentoTipo1({} as IOptionsFormasPagamento);
    setFormasPagamento({} as IFormasPagamento);
  }, []);

  return (
    <FormaPagamentoContext.Provider
      value={{
        loading,
        formasPagamento,
        optionsFormasPagamento,
        optionsFormasPagamentoTipo1,
        buscaFormaPagamento,
        limparFormaPagamento,
      }}
    >
      {children}
    </FormaPagamentoContext.Provider>
  );
};

function useFormaPagamento(): FormaPagamentoContextData {
  const context = useContext(FormaPagamentoContext);

  if (!context) {
    throw new Error(
      'useFormaPagamento deve ser usado com o FormaPagamentoProvider',
    );
  }

  return context;
}

export { FormaPagamentoProvider, useFormaPagamento };
